/* ============================================================
   The chain step — the one place rrule.js is touched.

   A recurring task is a single live row carrying its own RRULE and its current
   day (the Todoist / Apple Reminders model): resolving it advances that row to
   the next day the rule fires on. So the only question the recurrence engine has
   to answer is "given this rule and this anchor, what's the next day?" — no
   expansion, no occurrence merge, no exception rows.

   Days are floating-local "YYYY-MM-DD"; we anchor rrule.js at UTC midnight so
   the arithmetic is offset/DST-safe (see dates.ts) — a floating all-day
   recurrence is exactly what the iCalendar spec calls a DATE-valued rule.
   ============================================================ */
import { RRule } from 'rrule';
import { utcToYMD, ymdToUTC } from './dates';

/**
 * The first day this rule fires at-or-after `after` (or strictly after, with
 * `inclusive: false`), or null when the rule is exhausted (an UNTIL/COUNT bound
 * inside the RRULE string ended the series).
 *
 * `anchor` is the rule's DTSTART — the task's current day. It carries the phase
 * for the rules that need one: `FREQ=DAILY;INTERVAL=3` counts from it, and
 * `FREQ=MONTHLY` takes its day-of-month from it. (`BYDAY` rules ignore it.)
 */
export function nextOccurrenceAfter(
  rrule: string,
  anchor: string,
  after: string,
  inclusive = false,
): string | null {
  const options = RRule.parseString(rrule);
  options.dtstart = ymdToUTC(anchor);
  const next = new RRule(options).after(ymdToUTC(after), inclusive);
  return next ? utcToYMD(next) : null;
}

/**
 * The day a recurring task moves to when its current occurrence is resolved
 * (completed or skipped) on `today`. Null ends the chain.
 *
 * The search starts from the later of `current` and `today`, so resolving a task
 * that sat unfinished for a week jumps to the next slot *ahead* rather than
 * walking through every day that was missed — the leaders' behaviour (Todoist,
 * Reminders), and the kinder one: a missed day is a fresh start, not a backlog
 * to grind through. The phase still comes from `current`, so an every-3-days
 * task stays on its original rhythm.
 *
 * Resolving an OVERDUE task (`current < today`) searches inclusive of `today` —
 * a daily task skipped three days late reappears today, not tomorrow, since
 * today is itself a valid, unresolved slot. Resolving a task on (or before) its
 * own day searches strictly after `current`, so completing today's occurrence
 * of a daily task still advances instead of standing still.
 */
export function nextChainDay(rrule: string, current: string, today: string): string | null {
  const after = current > today ? current : today;
  const inclusive = after > current;
  return nextOccurrenceAfter(rrule, current, after, inclusive);
}
